<template>
  <div>
    <div class="mask" v-if="isSearchActive">
      <div class="contents">
        <div class="search-header">
          <form class="search-bar" @submit.prevent="doSearch()">
            <input
              type="search"
              v-model="query"
              placeholder="Search in version (text or #123 for ID)"
              autofocus
              ref="searchField"
            />

            <font-awesome-icon icon="spinner" v-if="isLoading" />

            <!-- So the form also submits with enter. -->
            <button type="submit" hidden />
          </form>

          <span class="close" @click="isSearchActive = false">
            <font-awesome-icon icon="times" class="close" />
          </span>
        </div>

        <section class="results">
          <h1>Questions</h1>
          <search-modal-results :results="questionResults" />
        </section>
        <section class="results">
          <h1>Rules</h1>
          <search-modal-results :results="ruleResults" />
        </section>
        <section class="results">
          <h1>Report</h1>
          <search-modal-results :results="reportResults" />
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import SearchModalResults from '@/components/SearchModalResults.vue';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { searchAll } from '@/api';
import { debounce } from 'lodash';
import { useRoute } from 'vue-router/composables';

const props = defineProps({
  // Whether the modal can be made active. This doesn't mean that
  // the modal is visible, just that it *might* be.
  enabled: Boolean,

  // This determines whether the search modal is actually shown.
  // Should never be set to true if enabled = false.
  value: {
    type: Boolean,
    required: false,
  },

  // The version within which to search
  version: Object,
});

// Emits when the isSearchActive value changes.
const emit = defineEmits(['input']);

const query = ref('');
const isLoading = ref(0);
const results = ref(null);

const searchField = ref(null);

let sequence = 0;

// Queries the API with the current search term
async function doSearch() {
  const q = query.value;
  if (q.length < 2) {
    // Don't search under two characters.
    results.value = null;
    return;
  }

  try {
    const nr = ++sequence;
    isLoading.value++;

    const response = (await searchAll(props.version, query.value)).data;

    if (sequence === nr) {
      // If another request is already underway, wait for
      // that one instead.
      results.value = response;
    }
  } finally {
    isLoading.value--;
  }
}

const searchDebounced = debounce(doSearch, 500, {
  maxWait: 2000,
});

// Computed properties with results. Each individual result
// is an object with a "link" property and a "tokens" property.
// Each token has a "type" and a "value". The type is either
// "text" for a regular token or "match" for a highlighted
// token that matches the search query. A third type, "language",
// is used for tokens that indicate the language of a set of
// tokens that comes after it.
const questionResults = computed(() => {
  const v = results.value;
  const out = [];
  if (!v) {
    return out;
  }

  const query = v.q;
  for (const section of v.question_sections) {
    const langs = segmentTranslations(section.title, query);

    out.push({
      link: {
        name: 'questions',
        params: {
          versionId: props.version.id,
          sectionId: section.id,
        },
      },
      tokens: [
        // Segment the identifier in case reference search is used.
        ...segmentTokens(`QS#${section.id} - `, query),
        ...combineTranslations(langs),
      ],
    });
  }

  for (const question of v.questions) {
    // Fields of interest are text, explanation, and the text of each option.
    const tokens = segmentTokens(`Q#${question.id} - `, query);

    // Question text tokens, always show at least one language.
    const textLangs = segmentTranslations(question.text, query);
    const textTokens = combineTranslations(textLangs);
    tokens.push(...textTokens);

    // If there's no match in explanations, don't show it at all; we'll
    // get empty tokens back from here.
    const explanationLangs = segmentTranslations(question.explanation, query);
    const explanationTokens = combineTranslations(explanationLangs, null);
    if (explanationTokens.length) {
      tokens.push(
        {
          type: 'text',
          value: ' (',
        },
        ...explanationTokens,
        {
          type: 'text',
          value: ')',
        }
      );
    }

    // Also just include the options with matches, and only
    // the languages with matches.
    const options = [];
    for (const option of question.options ?? []) {
      const optionLangs = segmentTranslations(option.text, query);
      const optionTokens = combineTranslations(optionLangs, null);

      if (optionTokens.length) {
        options.push(
          {
            type: 'text',
            value: ' [',
          },
          ...optionTokens,
          {
            type: 'text',
            value: ']',
          }
        );
      }
    }

    tokens.push(...options);

    out.push({
      link: {
        name: 'questions',
        params: {
          versionId: props.version.id,
          sectionId: question.section.id,
          questionId: question.id,
        },
      },
      tokens: tokens,
    });
  }

  return out;
});

// Matching rules
const ruleResults = computed(() => {
  const v = results.value;
  const out = [];
  if (!v) {
    return out;
  }
  const query = v.q;
  for (const section of v.rule_sections) {
    out.push({
      link: {
        name: 'rules',
        params: {
          versionId: props.version.id,
          sectionId: section.id,
        },
      },
      tokens: [
        ...segmentTokens(`RS#${section.id} - `, query),
        ...segmentTokens(section.label, query),
      ],
    });
  }

  for (const rule of v.rules) {
    out.push({
      link: {
        name: 'rules',
        params: {
          versionId: props.version.id,
          sectionId: rule.section.id,
          ruleId: rule.id,
        },
      },
      tokens: [
        ...segmentTokens(`R#${rule.id} - `, query),
        ...segmentTokens(rule.label, query),
      ],
    });
  }

  return out;
});

const reportResults = computed(() => {
  const v = results.value;
  const out = [];
  if (!v) {
    return out;
  }

  const query = v.q;
  for (const section of v.template_sections) {
    const langs = segmentTranslations(section.label, query);

    out.push({
      link: {
        name: 'templates',
        params: {
          versionId: props.version.id,
          sectionId: section.id,
        },
      },
      tokens: [
        ...segmentTokens(`DS#${section.id} - `, query),
        ...combineTranslations(langs),
      ],
    });
  }

  for (const template of v.templates) {
    // Fields of interest are the label, and the label/value of each line
    const tokens = segmentTokens(`D#${template.id} - `, query);

    // Question text tokens, always show at least one language.
    const labelLangs = segmentTranslations(template.label, query);
    const labelTokens = combineTranslations(labelLangs);
    tokens.push(...labelTokens);

    const lines = [];
    for (const line of template.lines ?? []) {
      // If a line label / value has a match, we include the whole label: value
      // pair. If it doesn't, we ignore it entirely.
      const lineLabelLangs = segmentTranslations(line.label, query);
      const lineValueLangs = segmentTranslations(line.value, query);

      let lineLabelTokens = combineTranslations(lineLabelLangs, null);
      let lineValueTokens = combineTranslations(lineValueLangs, null);

      if (!lineLabelTokens.length && !lineValueTokens.length) {
        continue;
      }

      if (!lineLabelTokens.length) {
        // Request a fallback this time
        lineLabelTokens = combineTranslations(lineLabelLangs);
      }

      if (!lineValueTokens.length) {
        // Request a fallback this time
        lineValueTokens = combineTranslations(lineValueLangs);
      }

      lines.push(
        {
          type: 'text',
          value: ' [',
        },
        ...lineLabelTokens
      );

      if (lineLabelTokens.length && lineValueTokens.length) {
        // There still may not be a label *and* a value, only include
        // the separator token if there are.
        lines.push({
          type: 'text',
          value: ': ',
        });
      }

      lines.push(...lineValueTokens);

      lines.push({
        type: 'text',
        value: ']',
      });
    }

    tokens.push(...lines);
    out.push({
      link: {
        name: 'templates',
        params: {
          versionId: props.version.id,
          sectionId: template.section.id,
          templateId: template.id,
        },
      },
      tokens: tokens,
    });
  }

  return out;
});

// Takes an input search string and segments it into either match or
// text tokens.
function segmentTokens(input, query) {
  if (!query) return [{ value: input, type: 'text' }];

  const tokens = [];

  // Our results come from a MySQL LIKE query, which ignores accents,
  // diacritics, etc. To correctly split our tokens, we'll have to do
  // the same here. We'll still retrieve our characters from the original
  // input, so we can match the correct token without having to display
  // the normalized token.
  const normalize = (str) =>
    str
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const normalizedInput = normalize(input);
  const normalizedQuery = normalize(query).replace(
    // Escape regex special characters in our query.
    /[.*+?^${}()|[\]\\]/g,
    '\\$&'
  );

  // Escape regex characters in the query
  const regex = new RegExp(normalizedQuery, 'gi');
  let lastIndex = 0;
  let match;

  // Iterate all exact matches of the query string
  while ((match = regex.exec(normalizedInput)) !== null) {
    if (match.index > lastIndex) {
      // Push everything before the match as a regular token
      tokens.push({ value: input.slice(lastIndex, match.index), type: 'text' });
    }

    // Push the match as a match token with matching casing. Note that
    // we slice from the original input rather than using the match text.
    tokens.push({
      value: input.slice(match.index, regex.lastIndex),
      type: 'match',
    });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < input.length) {
    tokens.push({ value: input.slice(lastIndex), type: 'text' });
  }

  return tokens;
}

// Produces segmented tokens for each language
function segmentTranslations(input, query) {
  const out = {};
  for (const lang in input) {
    out[lang] = segmentTokens(input[lang] ?? '', query);
  }

  return out;
}

// Takes an object with language keys and a list of
// segmented token values. Adds values for all the languages
// that have a matching token.
function combineTranslations(segmented, fallback = 'nl') {
  const out = [];
  const languageToken = (lang) => ({
    value: `(${lang.toUpperCase()}:)`,
    type: 'language',
  });

  for (const lang in segmented) {
    // If we have no matches, ignore this language.
    if (!segmented[lang].some((token) => token.type === 'match')) {
      continue;
    }

    if (out.length) {
      out.push({
        value: ', ',
        type: 'text',
      });
    }

    out.push(languageToken(lang));
    out.push(...segmented[lang]);
  }

  if (!out.length && fallback && segmented[fallback]?.length) {
    // The match occurred somewhere else; show the Dutch value instead.
    // Note: if the text value is empty, we still don't push anything.
    out.push(languageToken(fallback));
    out.push(...segmented[fallback]);
  }

  return out;
}

watch(
  () => query.value,
  () => searchDebounced(),
  {
    flush: 'post',
  }
);

onMounted(() => {
  window.addEventListener('keydown', handleSearchKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleSearchKeyDown);
});

//#region Modal open / close
const route = useRoute();

// The search active property is the v-model of this component. This
// way it can be enabled externally by binding a variable.
const _searchActive = ref(false);
const isSearchActive = computed({
  get: () => _searchActive.value,
  set(v) {
    if (v && !props.enabled) {
      console.error('Activating search with enabled=false is invalid.');
      v = false;
    }

    if (_searchActive.value === v) {
      return;
    }

    _searchActive.value = v;
    emit('input', _searchActive.value);
  },
});

watch(
  () => props.value,
  (v) => {
    if (typeof v === 'boolean') {
      isSearchActive.value = v;
    }
  },
  { immediate: true }
);

function handleSearchKeyDown(event) {
  if (!props.enabled) {
    return;
  }

  if (event.key === 'Escape') {
    isSearchActive.value = false;
    event.preventDefault();
    return;
  }

  const isInputFocused = event.target.matches(
    'input, textarea, [contenteditable="true"]'
  );
  if (isInputFocused) {
    // Ignore when in an input item.
    return;
  }

  if (event.key === '/') {
    isSearchActive.value = true;
    event.preventDefault();
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleSearchKeyDown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleSearchKeyDown);
});

// If the route changes, close the search window.
watch(
  () => route.fullPath,
  () => (isSearchActive.value = false)
);

watch(
  () => props.enabled,
  (enabled) => {
    if (!enabled) {
      isSearchActive.value = false;
    }
  }
);

watch(
  () => isSearchActive.value,
  (active) => {
    if (active) {
      // Focus the search field when made active
      nextTick(() => searchField.value?.focus());

      // If a previous query is present, refresh it.
      doSearch();
    } else {
      // Clear the results when search was made inactive
      results.value = null;
    }
  }
);
//#endregion
</script>

<style scoped lang="scss">
.mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.search-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.search-bar {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  justify-content: flex-start;
  align-items: center;
}

.contents {
  background-color: #fff;
  width: 90%;
  min-width: 640px;
  min-height: 300px;
  max-height: 90%;
  overflow: auto;
  padding: 15px;
}

h1 {
  font-size: 1.4rem;
  color: #4e4e4e;
  padding: 0;
  margin: 0 0 0.5rem 0;
}

input {
  border-radius: 3px;
  border: 1px solid lightgrey;
  font-size: 1rem;
  padding: 0.5rem;
  margin: 0;
  outline-style: none;
  width: 300px;
}

.results {
  margin-top: 1rem;
}

.close {
  cursor: pointer;
}
</style>
